import {
    COST_METRIC_NAME,
    CLICKS_METRIC_NAME,
    IMPRESSIONS_METRIC_NAME,
    CONVERSIONS_METRIC_NAME,
    CPA_METRIC_NAME,
    ROAS_METRIC_NAME,
    CTR_METRIC_NAME,
} from "../../dashboard/metrics";


export const SUGGESTED_IDS = [
    COST_METRIC_NAME, CLICKS_METRIC_NAME, IMPRESSIONS_METRIC_NAME, CONVERSIONS_METRIC_NAME,
    CPA_METRIC_NAME, ROAS_METRIC_NAME, CTR_METRIC_NAME,
];

// Orden curado para que las categorías "core" aparezcan primero (Performance, Goals, Presupuesto)
// y los canales después. Las categorías no listadas caen al final por orden alfabético.
export const CATEGORY_ORDER = [
    'PERFORMANCE',
    'BUSINESS_UNIT_INDICATORS_GOALS',
    'BUSINESS_UNIT_INDICATORS_BUDGET',
    'GOOGLE',
    'META',
    'TIKTOK',
    'LINKEDIN',
    'DV360',
    'GA4',
    'GSC',
    'ECOMMERCE',
    'KLAVIYO',
    'BUSINESS_UNIT',
    '__CUSTOM__',
];

const CATEGORY_INDEX = (() => {
    const map = {};
    CATEGORY_ORDER.forEach((k, i) => { map[k] = i; });
    return map;
})();

// Visual por categoría (logo de canal o ícono temático para grupos transversales).
// El "group" del catálogo del Data Node ya nos dice a qué canal/sección pertenece la
// métrica — no parseamos el displayName. Si el group no está acá, no se muestra ícono
// y la fila queda con el slot vacío (mantiene la alineación).
export const CHANNEL_VISUAL = {
    GOOGLE:    {prefix: 'fab', iconName: 'google',                 color: '#1AA85D'},
    META:      {prefix: 'fab', iconName: 'meta',                   color: '#3B5998'},
    TIKTOK:    {prefix: 'fab', iconName: 'tiktok',                 color: '#000000'},
    LINKEDIN:  {prefix: 'fab', iconName: 'linkedin',               color: '#0A66C2'},
    DV360:     {prefix: 'fab', iconName: 'google',                 color: '#34A853'},
    GA4:       {prefix: 'fas', iconName: 'chart-bar',              color: '#E7B042'},
    GSC:       {prefix: 'fas', iconName: 'magnifying-glass-chart', color: '#4285F4'},
    KLAVIYO:   {prefix: 'fas', iconName: 'envelope',               color: '#1B1B1B'},
    ECOMMERCE: {prefix: 'fas', iconName: 'shopping-bag',           color: '#96BF48'},
    PERFORMANCE:                     {prefix: 'fas', iconName: 'chart-line',           color: '#4A4A4A'},
    BUSINESS_UNIT_INDICATORS_GOALS:  {prefix: 'fas', iconName: 'bullseye',             color: '#FF470F'},
    BUSINESS_UNIT_INDICATORS_BUDGET: {prefix: 'fas', iconName: 'gauge-high',           color: '#7CB502'},
    BUSINESS_UNIT:                   {prefix: 'fas', iconName: 'cog',                  color: '#4A4A4A'},
    '__CUSTOM__':                    {prefix: 'fas', iconName: 'wand-magic-sparkles',  color: '#440631'},
};

export function getChannelVisual(group) {
    return CHANNEL_VISUAL[group] || null;
}

// Prefijos canónicos que el backend antepone al displayName cuando la métrica pertenece
// a un canal específico (ej. "TikTok - Costo por seguidor"). Los removemos en la UI
// porque el logo + microcopy ya comunican el canal — repetirlo es ruido.
// Es derivado del `group` del catálogo, no parseo heurístico del string.
const CHANNEL_PREFIXES = {
    GOOGLE:    ['Google Ads', 'Google'],
    META:      ['Meta Ads', 'Meta', 'Facebook'],
    TIKTOK:    ['TikTok Ads', 'TikTok'],
    LINKEDIN:  ['LinkedIn Ads', 'LinkedIn'],
    DV360:     ['DV360'],
    GA4:       ['Google Analytics 4', 'GA4'],
    GSC:       ['Search Console', 'GSC'],
    KLAVIYO:   ['Klaviyo'],
    ECOMMERCE: ['Ecommerce', 'Shopify', 'Tiendanube', 'Tienda Nube', 'WooCommerce', 'VTEX'],
};

export function stripChannelPrefix(displayName, group) {
    if (!displayName) return displayName;
    const prefixes = CHANNEL_PREFIXES[group];
    if (!prefixes || prefixes.length === 0) return displayName;
    const dnLower = displayName.toLowerCase();
    for (const p of prefixes) {
        const sep = (p + ' - ').toLowerCase();
        if (dnLower.startsWith(sep)) {
            return displayName.slice(p.length + 3);
        }
    }
    return displayName;
}

// Cuando el usuario tipea X, expandimos a Y. Pensado para términos comunes que
// no matchean los displayNames canónicos (p.ej. "inversión" → "costo" porque el
// display name oficial es "Costo", no "Inversión").
export const SEARCH_ALIASES = {
    'inversion':  ['costo', 'spend', 'inversion'],
    'inversiones':['costo', 'spend'],
    'gasto':      ['costo', 'spend'],
    'gastos':     ['costo', 'spend'],
    'spend':      ['costo'],
    'cost':       ['costo'],
    'compra':     ['conversion', 'compras', 'purchase', 'venta'],
    'compras':    ['conversion', 'compras', 'purchase', 'venta'],
    'ventas':     ['purchase', 'compras', 'revenue', 'venta'],
    'venta':      ['purchase', 'compras', 'revenue'],
    'click':      ['clic'],
    'clicks':     ['clic', 'clics'],
    'imp':        ['impresion'],
    'impresion':  ['impresion', 'impression'],
    'impressions':['impresion'],
    'rev':        ['revenue', 'ingreso', 'compras'],
    'revenue':    ['revenue', 'ingreso'],
    'conv':       ['conversion'],
    'conversiones':['conversion'],
};

export function normalize(str) {
    if (str === null || str === undefined) return '';
    return str
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

function categoryRank(groupKey) {
    const idx = CATEGORY_INDEX[groupKey];
    return idx !== undefined ? idx : 1000;
}

// Score sobre un solo término. Devuelve 0 si no matchea.
function scoreTerm(term, displayNameNorm, idNorm, groupLabelNorm) {
    if (!term) return 0;
    if (displayNameNorm === term) return 1000;
    if (displayNameNorm.startsWith(term)) return 800;
    // word-start: alguna palabra del displayName empieza con el término
    const words = displayNameNorm.split(/[\s\-_/(),]+/).filter(Boolean);
    if (words.some((w) => w.startsWith(term))) return 600;
    if (displayNameNorm.includes(term)) return 400;
    if (idNorm && idNorm.includes(term)) return 250;
    if (groupLabelNorm && groupLabelNorm.includes(term)) return 200;
    return 0;
}

function bestScoreForQuery(query, displayNameNorm, idNorm, groupLabelNorm) {
    const direct = scoreTerm(query, displayNameNorm, idNorm, groupLabelNorm);
    let best = direct;

    // Aliases: si la query (normalizada) matchea una key del alias map, probamos cada keyword
    // y nos quedamos con el mejor — pero con tope 750 para no superar un exact match real.
    const aliasKeywords = SEARCH_ALIASES[query];
    if (aliasKeywords) {
        for (const kw of aliasKeywords) {
            const s = scoreTerm(normalize(kw), displayNameNorm, idNorm, groupLabelNorm);
            if (s > 0) {
                best = Math.max(best, Math.min(s, 750));
            }
        }
    }
    return best;
}

// rankItems: ordena items según la query.
// items: [{id, displayName, group, groupLabel, isCustom, isBroken, ...}]
// query: string raw (no normalizada)
// Devuelve un nuevo array ordenado (no muta el original) con `_score` agregado para debug/highlight.
export function rankItems({items, query, suggestedIds = SUGGESTED_IDS}) {
    const q = normalize(query).trim();
    const suggestedSet = new Set(suggestedIds);

    if (!q) {
        // Sin búsqueda: orden por categoría + alfabético dentro de categoría.
        return items.slice().sort((a, b) => {
            const ra = categoryRank(a.group);
            const rb = categoryRank(b.group);
            if (ra !== rb) return ra - rb;
            return a.displayName.localeCompare(b.displayName, 'es', {sensitivity: 'base'});
        });
    }

    const scored = items.map((it) => {
        const dn = normalize(it.displayName);
        const idn = normalize(it.id);
        const gln = normalize(it.groupLabel || it.group);
        let score = bestScoreForQuery(q, dn, idn, gln);
        if (score === 0) return null;
        if (suggestedSet.has(it.id)) score += 60;
        if (it.group === 'PERFORMANCE') score += 30;
        if (it.isBroken) score -= 10;
        return {item: it, score};
    }).filter(Boolean);

    scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const ra = categoryRank(a.item.group);
        const rb = categoryRank(b.item.group);
        if (ra !== rb) return ra - rb;
        return a.item.displayName.localeCompare(b.item.displayName, 'es', {sensitivity: 'base'});
    });

    return scored.map((s) => s.item);
}

// Si el usuario buscó algo que no matchea pero conocemos un alias, sugerimos el primer keyword.
// Devuelve {label, queryToApply} o null.
export function getDidYouMean(query) {
    const q = normalize(query).trim();
    if (!q) return null;
    const aliasKeywords = SEARCH_ALIASES[q];
    if (!aliasKeywords || aliasKeywords.length === 0) return null;
    return {label: aliasKeywords[0], queryToApply: aliasKeywords[0]};
}

// Parte el displayName en [before, match, after] para resaltar el query.
// Devuelve null si no hay match (en cuyo caso se renderiza el label completo sin highlight).
export function splitForHighlight(displayName, query) {
    const q = normalize(query).trim();
    if (!q) return null;
    const dnNorm = normalize(displayName);
    const idx = dnNorm.indexOf(q);
    if (idx < 0) return null;
    return {
        before: displayName.slice(0, idx),
        match: displayName.slice(idx, idx + q.length),
        after: displayName.slice(idx + q.length),
    };
}
